import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, IconButton, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { MdClose } from 'react-icons/md';
import { getData, postData } from '../../Webservice/Webservice';
import { FONT, authToken, yellowBtn } from './productShared';

const sellerLabel = (s) => [s.business_name || s.name, s.sellerCode, s.email].filter(Boolean).join(' · ');
const supplierLabel = (s) => [s.name, s.supplierId].filter(Boolean).join(' · ');

/**
 * One dialog for changing who owns products, used on the product, seller and supplier pages.
 *   products : [{ _id, title }]   what is being moved
 *   mode     : 'both' | 'seller' | 'supplier'   which owner(s) can be changed here
 *   onDone   : called after a successful change so the page can reload
 */
export default function ChangeOwnerDialog({ open, products = [], onClose, onDone }) {

  const [sellers, setSellers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [ownerType, setOwnerType] = useState('seller'); // 'seller' | 'supplier' | 'own'
  const [seller, setSeller] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setOwnerType('seller'); setSeller(null); setSupplier(null); setError('');
    getData('seller/list?limit=1000&status=approved', null, authToken(), false).then((r) => setSellers(r?.seller || [])).catch(() => setSellers([]));
    getData('supplier/list?limit=1000', null, authToken(), false).then((r) => setSuppliers(r?.suppliers || [])).catch(() => setSuppliers([]));
  }, [open]);

  const ready = ownerType === 'own' || (ownerType === 'seller' && seller) || (ownerType === 'supplier' && supplier);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { productIds: products.map((p) => p._id), ownerType };
      if (ownerType === 'seller') body.seller_id = seller._id;
      if (ownerType === 'supplier') body.supplier_id = supplier._id;
      await postData('product/change-owner', body, authToken(), false);
      onDone?.();
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not change the owner.');
    } finally {
      setSaving(false);
    }
  };

  const n = products.length;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>Change owner</Typography>
          <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{n === 1 ? products[0]?.title : `${n} products selected`}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}><MdClose size={20} /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>A product belongs to a seller <b>or</b> a supplier, not both.</Typography>
        <ToggleButtonGroup exclusive fullWidth size="small" value={ownerType} onChange={(_, v) => v && setOwnerType(v)}>
          <ToggleButton value="seller" sx={{ textTransform: 'none', fontWeight: 700 }}>Seller</ToggleButton>
          <ToggleButton value="supplier" sx={{ textTransform: 'none', fontWeight: 700 }}>Supplier</ToggleButton>
          <ToggleButton value="own" sx={{ textTransform: 'none', fontWeight: 700 }}>Our own</ToggleButton>
        </ToggleButtonGroup>
        {ownerType === 'seller' && (
          <Autocomplete size="small" options={sellers} value={seller} onChange={(_, v) => setSeller(v)} getOptionLabel={sellerLabel}
            isOptionEqualToValue={(a, b) => a._id === b._id} renderInput={(p) => <TextField {...p} label="Choose seller" />} />
        )}
        {ownerType === 'seller' && seller && (
          <Alert severity="info" sx={{ py: 0 }}>
            Will be assigned to <b>{seller.business_name || seller.name}</b> ({seller.sellerCode || 'no code'}) · {seller.email}<br />
            <span style={{ fontSize: 12 }}>Seller id …{String(seller._id).slice(-6)}</span>
          </Alert>
        )}
        {ownerType === 'supplier' && (
          <Autocomplete size="small" options={suppliers} value={supplier} onChange={(_, v) => setSupplier(v)} getOptionLabel={supplierLabel}
            isOptionEqualToValue={(a, b) => a._id === b._id} renderInput={(p) => <TextField {...p} label="Choose supplier" />} />
        )}
        {ownerType === 'own' && <Alert severity="info" sx={{ py: 0 }}>No seller or supplier is attached.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
        <Button onClick={save} disabled={!ready || saving} variant="contained" sx={yellowBtn}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}
